import { Button, FormHeading, FormSectionGroup, Layout } from '@vwfs-bronson/bronson-react';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AddressFormNew } from '../../../../../../components/AddressFormNew';
import { AddressFormModel_MX } from '../../../../../../components/AddressFormNew/mixed/formModel';
import { BankDetailsNew } from '../../../../../../components/BankDetailsNew';
import { CompanyDetailsForm_OB_FP } from '../../../../../../components/CompanyDetailsFormNew/business/otherBusiness/financialProduct/CompanyDetailsForm_OB_FP';
import { CustomerDetailsFormNew } from '../../../../../../components/CustomerDetailsFormNew';
import { DriverDetailsForm_OB_FP } from '../../../../../../components/DriverDetailsFormNew/business/otherBusiness/financialProduct/DriverDetailsForm_OB_FP';
import { LinkedRelationshipsForm_OB_FP } from '../../../../../../components/LinkedRelationshipsNew/business/otherBusiness/financialProduct/LinkedRelationships_OB_OL';
import { PreviousAddressFormNew } from '../../../../../../components/PreviousAddressFormNew';
import { isAtLeast3YearsApart } from '../../../../../../services/common/miscUtils';
import { journeyDataSelectors } from '../../../../../../services/redux/features/journeyData.slice';
import { useFormSectionHelpers } from '../../../../../utils';

const getCustomerFormsToRender = (showPreviousAddress: boolean, showBankDetails: boolean) => {
  return [
    'companyDetails',
    'driverDetails',
    ...(showBankDetails ? (['bankDetails'] as const) : []),
    'customerDetails1',
    'customerAddress1',
    ...(showPreviousAddress ? (['customerPreviousAddress1'] as const) : []),
    'linkedRelationships1',
  ] as const;
};

const getCoCustomerFormsToRender = (showPreviousAddress: boolean) => {
  return [
    'customerDetails2',
    'customerAddress2',
    ...(showPreviousAddress ? (['customerPreviousAddress2'] as const) : []),
    'linkedRelationships2',
  ] as const;
};

type FormId =
  | ReturnType<typeof getCustomerFormsToRender>[number]
  | ReturnType<typeof getCoCustomerFormsToRender>[number];

// type FormId = ReturnType<typeof getCustomerFormsToRender>[number];

export const CustomerData_FP_LtdPlcUnlimitedOther = () => {
  const [forms, setForms] = useState<ReadonlyArray<FormId>>(() => getCustomerFormsToRender(false, false));
  const [isAnotherHirerAdded, setIsAnotherHirerAdded] = useState(false);

  const [showPreviousAddress, setShowPreviousAddress] = useState(false);
  const [showPreviousCoCustomerAddress, setShowPreviousCoCustomerAddress] = useState(false);

  const toggleAnotherHirer = () => {
    setIsAnotherHirerAdded((isAnotherHirerAdded) => !isAnotherHirerAdded);
  };

  const isUpfrontPayment = useSelector(journeyDataSelectors.isUpfrontPayment);

  useEffect(() => {
    setForms([...getCustomerFormsToRender(!isUpfrontPayment, isAnotherHirerAdded)]);
  }, [isUpfrontPayment, isAnotherHirerAdded]);

  const getFormHelpers = useFormSectionHelpers(forms);

  const businessCustomerType = useSelector(journeyDataSelectors.businessCustomerType);

  const onCustomerAddressChange = (values: AddressFormModel_MX) => {
    const { timeAtAddressFrom, timeAtAddressTo } = values;
    if (!timeAtAddressFrom || !timeAtAddressTo) return;

    setShowPreviousAddress(!isAtLeast3YearsApart(timeAtAddressFrom, timeAtAddressTo));
  };

  const onCustomerAddress2Change = (values: AddressFormModel_MX) => {
    const { timeAtAddressFrom, timeAtAddressTo } = values;
    if (!timeAtAddressFrom || !timeAtAddressTo) return;

    setShowPreviousCoCustomerAddress(!isAtLeast3YearsApart(timeAtAddressFrom, timeAtAddressTo));
  };

  useEffect(() => {
    setForms([
      ...getCustomerFormsToRender(showPreviousAddress, !isUpfrontPayment),
      ...(isAnotherHirerAdded ? getCoCustomerFormsToRender(showPreviousCoCustomerAddress) : []),
    ]);
  }, [isAnotherHirerAdded, isUpfrontPayment, showPreviousAddress, showPreviousCoCustomerAddress]);

  // Helper for some more variable titles
  const getTitleTranslation = <T extends string>(fileName: T, isAnotherHirer = false): `${T}:title:${string}` => {
    return `${fileName}:title:${businessCustomerType}${isAnotherHirer ? 2 : 1}`;
  };

  return (
    <FormSectionGroup>
      <CompanyDetailsForm_OB_FP formHelpers={getFormHelpers('companyDetails')} />

      <DriverDetailsForm_OB_FP formHelpers={getFormHelpers('driverDetails')} />

      <BankDetailsNew.FP
        formHelpers={getFormHelpers('bankDetails')}
        titleTranslationKey="bankDetailsForm:title:business"
      />

      <CustomerDetailsFormNew.FP formHelpers={getFormHelpers('customerDetails1')} />

      <AddressFormNew.FP formHelpers={getFormHelpers('customerAddress1')} onValuesChange={onCustomerAddressChange} />

      {showPreviousAddress && <PreviousAddressFormNew.PL formHelpers={getFormHelpers('customerPreviousAddress1')} />}

      <LinkedRelationshipsForm_OB_FP
        formHelpers={getFormHelpers('linkedRelationships1')}
        titleTranslationKey={getTitleTranslation('linkedRelationshipsForm')}
      />

      {isAnotherHirerAdded && (
        <>
          <Layout>
            <Layout.Item default="1/2">
              <FormHeading>{t(`customerDataPage:customer2Header:${businessCustomerType}`)}</FormHeading>
            </Layout.Item>
          </Layout>

          <CustomerDetailsFormNew.FP formHelpers={getFormHelpers('customerDetails2')} />

          <AddressFormNew.FP
            formHelpers={getFormHelpers('customerAddress2')}
            onValuesChange={onCustomerAddress2Change}
          />

          {showPreviousCoCustomerAddress && (
            <PreviousAddressFormNew.PL formHelpers={getFormHelpers('customerPreviousAddress2')} />
          )}

          <LinkedRelationshipsForm_OB_FP
            formHelpers={getFormHelpers('linkedRelationships2')}
            titleTranslationKey={getTitleTranslation('linkedRelationshipsForm')}
          />
        </>
      )}

      <Layout className="u-mb">
        <Layout.Item>
          <Button
            icon={isAnotherHirerAdded ? 'semantic-minus' : 'semantic-plus'}
            link
            secondary
            onClick={toggleAnotherHirer}
          >
            {isAnotherHirerAdded
              ? t('customerDataPrivate:removeAnotherDirectorButton')
              : t('customerDataPrivate:addAnotherDirectorButton')}
          </Button>
        </Layout.Item>
      </Layout>
    </FormSectionGroup>
  );
};
